import type { IHotelList } from "@/feature/hotel/search/types/hotel-search-page-type";
import type { IHotelsSearchSchema } from "@/feature/hotel/search/types/hotel-search-type";

import type {
  CustomHotelSearchFormValues,
  CustomUmrahCapturedHotelPayload,
  CustomUmrahSelectedHotel,
} from "../custom-umrah.schema";
import type {
  CustomUmrahFlowState,
  SerializableCustomUmrahHotelSelection,
  SerializableHotelSearch,
} from "./custom-umrah-flow.slice";

const CUSTOM_UMRAH_FLOW_SESSION_KEY = "custom-umrah:flow:v1";

const EMPTY_CUSTOM_UMRAH_FLOW_STATE: CustomUmrahFlowState = {
  searches: {},
  selections: {},
  formDraft: {
    routeVehicles: [
      {
        routeId: "",
        vehicleId: "",
      },
    ],
    includeGuide: false,
    email: "",
    phone: "",
    customerNote: "",
  },
};

function toSerializableDate(value: Date | string): string {
  if (typeof value === "string") {
    return value;
  }

  if (Number.isNaN(value.getTime())) {
    return "";
  }

  return value.toISOString();
}

function toDateValue(value: string) {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function cloneSerializableHotelSearch(
  value: SerializableHotelSearch,
): SerializableHotelSearch {
  return {
    ...value,
    location: {
      ...value.location,
    },
    date: {
      from: value.date.from,
      to: value.date.to,
    },
    rooms: value.rooms.map((room) => ({
      adults: room.adults,
      children: room.children,
      infants: room.infants,
      children_ages: [...(room.children_ages ?? [])],
    })),
  };
}

function cloneSerializableHotelSelection(
  value: SerializableCustomUmrahHotelSelection,
): SerializableCustomUmrahHotelSelection {
  return {
    hotel: {
      ...value.hotel,
    },
    search: cloneSerializableHotelSearch(value.search),
    captured_payload: value.captured_payload
      ? {
        supplier: value.captured_payload.supplier,
        search_context: {
          search_id: value.captured_payload.search_context.search_id,
          checkin: value.captured_payload.search_context.checkin,
          checkout: value.captured_payload.search_context.checkout,
          nights: value.captured_payload.search_context.nights,
          rooms_requested:
            value.captured_payload.search_context.rooms_requested.map(
              (room) => ({
                adults: room.adults,
                children_ages: [...room.children_ages],
              }),
            ),
        },
        hotel: {
          ...value.captured_payload.hotel,
          geolocation: {
            latitude: value.captured_payload.hotel.geolocation.latitude,
            longitude: value.captured_payload.hotel.geolocation.longitude,
          },
        },
        selected_rate: {
          ...value.captured_payload.selected_rate,
          room_references: [
            ...value.captured_payload.selected_rate.room_references,
          ],
        },
        price: {
          ...value.captured_payload.price,
        },
        cancellation_policy: {
          ...value.captured_payload.cancellation_policy,
          details: value.captured_payload.cancellation_policy.details.map(
            (detail) => ({
              from_date: detail.from_date,
              fee: detail.fee,
            }),
          ),
        },
        captured_at: value.captured_payload.captured_at,
      }
      : undefined,
  };
}

function cloneFormDraft(state: CustomUmrahFlowState): CustomUmrahFlowState["formDraft"] {
  const routeVehicles = state.formDraft?.routeVehicles?.length
    ? state.formDraft.routeVehicles
    : EMPTY_CUSTOM_UMRAH_FLOW_STATE.formDraft.routeVehicles;

  return {
    routeVehicles: routeVehicles.map((item) => ({
      routeId: item.routeId ?? "",
      vehicleId: item.vehicleId ?? "",
    })),
    includeGuide: Boolean(state.formDraft?.includeGuide),
    email: typeof state.formDraft?.email === "string" ? state.formDraft.email : "",
    phone: typeof state.formDraft?.phone === "string" ? state.formDraft.phone : "",
    customerNote:
      typeof state.formDraft?.customerNote === "string"
        ? state.formDraft.customerNote
        : "",
  };
}

export function serializeHotelSearchData(
  search: IHotelsSearchSchema,
): SerializableHotelSearch {
  return {
    ...search,
    location: {
      ...search.location,
    },
    date: {
      from: toSerializableDate(search.date.from),
      to: toSerializableDate(search.date.to),
    },
    rooms: search.rooms.map((room) => ({
      adults: room.adults,
      children: room.children,
      infants: room.infants,
      children_ages: [...(room.children_ages ?? [])],
    })),
  };
}

export function serializeCustomUmrahHotelSelectionData(
  selection: {
    hotel: IHotelList;
    search: IHotelsSearchSchema;
    captured_payload?: CustomUmrahCapturedHotelPayload;
  },
): SerializableCustomUmrahHotelSelection {
  return {
    hotel: {
      ...selection.hotel,
    },
    search: serializeHotelSearchData(selection.search),
    captured_payload: selection.captured_payload
      ? {
        supplier: selection.captured_payload.supplier,
        search_context: {
          search_id: selection.captured_payload.search_context.search_id,
          checkin: selection.captured_payload.search_context.checkin,
          checkout: selection.captured_payload.search_context.checkout,
          nights: selection.captured_payload.search_context.nights,
          rooms_requested:
            selection.captured_payload.search_context.rooms_requested.map(
              (room) => ({
                adults: room.adults,
                children_ages: [...room.children_ages],
              }),
            ),
        },
        hotel: {
          ...selection.captured_payload.hotel,
          geolocation: {
            latitude: selection.captured_payload.hotel.geolocation.latitude,
            longitude:
              selection.captured_payload.hotel.geolocation.longitude,
          },
        },
        selected_rate: {
          ...selection.captured_payload.selected_rate,
          room_references: [
            ...selection.captured_payload.selected_rate.room_references,
          ],
        },
        price: {
          ...selection.captured_payload.price,
        },
        cancellation_policy: {
          ...selection.captured_payload.cancellation_policy,
          details: selection.captured_payload.cancellation_policy.details.map(
            (detail) => ({
              from_date: detail.from_date,
              fee: detail.fee,
            }),
          ),
        },
        captured_at: selection.captured_payload.captured_at,
      }
      : undefined,
  };
}

export function deserializeHotelSearchData(
  search?: SerializableHotelSearch,
): CustomHotelSearchFormValues | undefined {
  if (!search) {
    return undefined;
  }

  const from = toDateValue(search.date.from);
  const to = toDateValue(search.date.to);

  if (!from || !to || to <= from) {
    return undefined;
  }

  return {
    ...search,
    location: {
      ...search.location,
    },
    date: {
      from,
      to,
    },
    rooms: search.rooms.map((room) => ({
      adults: room.adults,
      children: room.children,
      infants: room.infants,
      children_ages: [...(room.children_ages ?? [])],
    })),
  };
}

export function deserializeCustomUmrahHotelSelectionData(
  selection?: SerializableCustomUmrahHotelSelection,
): CustomUmrahSelectedHotel | undefined {
  if (!selection) {
    return undefined;
  }

  const search = deserializeHotelSearchData(selection.search);
  if (!search) {
    return undefined;
  }

  return {
    hotel: {
      ...selection.hotel,
    },
    search,
    captured_payload: selection.captured_payload
      ? {
        supplier: selection.captured_payload.supplier,
        search_context: {
          search_id: selection.captured_payload.search_context.search_id,
          checkin: selection.captured_payload.search_context.checkin,
          checkout: selection.captured_payload.search_context.checkout,
          nights: selection.captured_payload.search_context.nights,
          rooms_requested:
            selection.captured_payload.search_context.rooms_requested.map(
              (room) => ({
                adults: room.adults,
                children_ages: [...room.children_ages],
              }),
            ),
        },
        hotel: {
          ...selection.captured_payload.hotel,
          geolocation: {
            latitude: selection.captured_payload.hotel.geolocation.latitude,
            longitude: selection.captured_payload.hotel.geolocation.longitude,
          },
        },
        selected_rate: {
          ...selection.captured_payload.selected_rate,
          room_references: [
            ...selection.captured_payload.selected_rate.room_references,
          ],
        },
        price: {
          ...selection.captured_payload.price,
        },
        cancellation_policy: {
          ...selection.captured_payload.cancellation_policy,
          details: selection.captured_payload.cancellation_policy.details.map(
            (detail) => ({
              from_date: detail.from_date,
              fee: detail.fee,
            }),
          ),
        },
        captured_at: selection.captured_payload.captured_at,
      }
      : undefined,
  };
}

export function loadCustomUmrahFlowStateFromSession(): CustomUmrahFlowState {
  if (typeof window === "undefined") {
    return EMPTY_CUSTOM_UMRAH_FLOW_STATE;
  }

  try {
    const raw = window.sessionStorage.getItem(CUSTOM_UMRAH_FLOW_SESSION_KEY);
    if (!raw) {
      return EMPTY_CUSTOM_UMRAH_FLOW_STATE;
    }

    const parsed = JSON.parse(raw) as CustomUmrahFlowState;

    return {
      searches: {
        makkah: parsed.searches?.makkah
          ? cloneSerializableHotelSearch(parsed.searches.makkah)
          : undefined,
        madinah: parsed.searches?.madinah
          ? cloneSerializableHotelSearch(parsed.searches.madinah)
          : undefined,
      },
      selections: {
        makkah: parsed.selections?.makkah
          ? cloneSerializableHotelSelection(parsed.selections.makkah)
          : undefined,
        madinah: parsed.selections?.madinah
          ? cloneSerializableHotelSelection(parsed.selections.madinah)
          : undefined,
      },
      formDraft: cloneFormDraft(parsed),
    };
  } catch {
    window.sessionStorage.removeItem(CUSTOM_UMRAH_FLOW_SESSION_KEY);
    return EMPTY_CUSTOM_UMRAH_FLOW_STATE;
  }
}

export function saveCustomUmrahFlowStateToSession(
  state: CustomUmrahFlowState,
): boolean {
  if (typeof window === "undefined") {
    return true;
  }

  const hasSearches = Boolean(state.searches.makkah || state.searches.madinah);
  const hasSelections = Boolean(
    state.selections.makkah || state.selections.madinah,
  );
  const hasFormDraft = Boolean(
    state.formDraft.routeVehicles.some(
      (item) => Boolean(item.routeId) || Boolean(item.vehicleId),
    ) ||
    state.formDraft.includeGuide ||
    state.formDraft.email.trim() ||
    state.formDraft.phone.trim() ||
    state.formDraft.customerNote.trim(),
  );

  if (!hasSearches && !hasSelections && !hasFormDraft) {
    window.sessionStorage.removeItem(CUSTOM_UMRAH_FLOW_SESSION_KEY);
    return true;
  }

  try {
    window.sessionStorage.setItem(
      CUSTOM_UMRAH_FLOW_SESSION_KEY,
      JSON.stringify(state),
    );
    return true;
  } catch {
    return false;
  }
}
